import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  cancelReserved,
  deleteVolatilityMessage,
  getVolatilityMessages,
  getVolatilityMessageSummary
} from "../../api/message/volatility-message.api";
import { Button, CalendarBar, Pagination, Tab, Tag } from "omoplay";
import SuspensePopup from "../SuspensePopup";
import MessageWrapPreview from "../volatility-message/content/preview/MessageWrapPreview";
import { AlertTriangle, EllipsisVertical, Info } from "lucide-react";

/**
 * 한 페이지에 보여줄 메시지 수. API 요청(size)과 Pagination(pageSize) 이 **같은 값**을 써야 한다.
 *
 * 2025-10-16 "pagination size 수정" 커밋에서 `rowSize={20}` 을 `pageSize={20}` 으로 바꾸자,
 * 그때까지 무시되던 20 이 살아나 페이지 수는 ceil(총건수/20) 으로 줄었는데 API 는 여전히 10건씩
 * 받아 왔다. 38건인 채널은 2페이지·20건만 보이고 21~38번째는 어느 페이지에서도 열 수 없었다
 * (고객사 신고: "목록이 2페이지뿐이고 과거 데이터가 안 보인다"). 숫자를 한 곳에만 둔다.
 */
const PAGE_SIZE = 10;

/** 아직 발송이 끝나지 않은 상태 — 분모가 실적이 아니라 '예상 대상 수'다. */
const isBeforeSend = (status) => status === "RESERVED" || status === "SENDING";

/**
 * 회원(친구톡) + 비회원(모먼트) 발송 수 합산.
 *
 * 모먼트는 successCount/sendCount 가 아니라 moment_* 컬럼에 따로 쌓인다.
 * 회원 분모(sendCount)는 **발송이 끝난 뒤에만** 기록되므로 예약 중에는 0 이다.
 * 예상 발송 회원 수는 예약 시점에 targetCount(volatility_message_target 행 수)로 확정돼 있으니
 * 실적이 없을 때 그 값을 분모로 쓴다 — 예약 건이 0/0 으로 보여 "예약 실패"로 오해받던 문제.
 */
const sendSummaryOf = (message) => {
  const success = (message.successCount ?? 0) + (message.momentSuccessSendCount ?? 0);
  const memberSend = (message.sendCount ?? 0) > 0 ? message.sendCount : (message.targetCount ?? 0);
  return { success, total: memberSend + (message.momentSendCount ?? 0) };
};

/**
 * 상태 태그. 발송 시각은 **상태와 한 묶음**으로 보여 준다(2026-10-15 결정).
 *
 * 종전에는 날짜 열이 발송일이었고 예약 건에는 캘린더 아이콘이 따로 붙어 있었다. 아이콘이 상태인지 버튼인지
 * 헷갈렸고, 정렬을 생성일순으로 바꾸면 날짜 열이 뒤섞여 보였다. 이제 날짜 열은 항상 생성일, 발송(예정)
 * 시각은 상태 태그 옆에만 적는다. 임시저장·분석중은 발송 시각이 의미가 없어 적지 않는다.
 */
/*
 * 색은 대시보드와 **같은 체계**를 쓴다(omoplay Tag: success=초록, info=파랑, error=빨강, warning=노랑):
 *   발송 예약=초록 · 발송 완료/발송 중=파랑 · 발송 실패=빨강 · 임시 저장=노랑 · 코드 미적재 주의=주황(별도 배지)
 * 종전에는 예약이 파랑, 완료가 회색, 발송 중이 초록이라 대시보드 숫자 색과 어긋났다.
 */
const STATUS = {
  RESERVED:  { label: "발송 예약", color: "success", showTime: true },
  SENDING:   { label: "발송 중",   color: "info",    showTime: true },
  SUCCEED:   { label: "발송 완료", color: "info",    showTime: true },
  FAILED:    { label: "발송 실패", color: "error",   showTime: true },
  TEMPORARY: { label: "임시 저장", color: "warning", showTime: false },
  COUNTING:  { label: "집계 중",   color: "default", showTime: false },
  ANALYZE:   { label: "분석 중",   color: "default", showTime: false },
};

const formatDateTime = (value) => moment(value).format("YYYY.MM.DD HH:mm");
const formatSendTime = (value) => moment(value).format("MM.DD HH:mm");
/** 요일 포함(한국어 로케일은 App.js 에서 전역 로드). 대시보드 '다음 발송' 처럼 한 건만 보여줄 때 쓴다. */
const formatSendTimeWithDay = (value) => moment(value).format("MM.DD (ddd) HH:mm");

const messageTypeLabel = (type) => {
  if (type === "TEXT" || type === "TEXT_IMAGE") return "기본 텍스트형";
  if (type === "WIDE_IMAGE") return "와이드 이미지형";
  if (type === "LIST_IMAGE") return "와이드 리스트형";
  if (type === "CAROUSEL") return "캐러셀 피드형";
  return type;
};

/**
 * (i) 안내 팝오버의 내용. 보여줄 것이 없으면 빈 배열이지만, 미리보기는 항상 있으므로 (i) 는 항상 그린다.
 * tone: "error"(실패) / "warn"(코드 부족) / 기본.
 */
const infoItemsOf = (message) => {
  const items = [];

  if (message.codeShortText) {
    items.push({
      key: "code-short",
      tone: "warn",
      title: "쿠폰 코드가 적재되지 않았습니다",
      body: message.codeShortText + "\n\n발송일 전까지 혜택에 코드를 적재해야 합니다. 부족한 만큼은 발송되지 않고 발급 실패로 남습니다.",
    });
  }

  if (message.status === "FAILED") {
    items.push({
      key: "error",
      tone: "error",
      title: "발송에 실패했습니다",
      body: message.errorText || "실패 사유가 기록되지 않았습니다. 고객센터로 문의해 주세요.",
    });
  }

  if (message.isSendMoment) {
    items.push({
      key: "moment",
      title: "회원이 아닌 친구에게도 보내는 메시지입니다",
      body: "카카오톡 채널을 추가하지 않은 사용자에게 카카오 모먼트로 발송됩니다. 회원 발송과 단가가 다릅니다.",
    });
  }

  if (message.status === "ANALYZE") {
    items.push({
      key: "analyze",
      title: "분석 중입니다",
      body: "AI 가 발송 대상을 분석하고 있습니다. 분석이 끝나면 임시 저장 상태로 바뀝니다.",
    });
  }

  const { success, total } = sendSummaryOf(message);
  if (isBeforeSend(message.status) && total > 0) {
    items.push({
      key: "expected",
      title: `발송 예정 대상 ${total.toLocaleString()}명`,
      body: "아직 발송 전이라 예약 시점 기준 예상 수량입니다. 실제 발송 시점의 회원 수·친구 추가 상태에 따라 최종 발송 수량은 달라질 수 있습니다.",
    });
  } else if (!isBeforeSend(message.status) && total > 0) {
    items.push({
      key: "result",
      title: `발송 ${success.toLocaleString()} / ${total.toLocaleString()}건 (${Math.round(success / total * 100)}%)`,
      body: "회원(친구톡)과 비회원(모먼트) 발송을 합한 수치입니다.",
    });
  }

  return items;
};

/* ───────────────────────── 대시보드 ───────────────────────── */

/**
 * 목록 상단 3박스. 숫자는 모두 **메시지 건수**이고 상단 기간 필터(생성일 기준)를 따른다.
 * summary 가 null 이면(조회 실패) 박스를 비워 두되 목록은 막지 않는다.
 */
const SummaryCards = ({ summary }) => {
  const n = (v) => (v ?? 0).toLocaleString();
  const Card = ({ title, children }) => (
      <div className="flex-1 min-w-[220px] bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="text-sm font-semibold text-gray-500 mb-3">{title}</div>
        {children}
      </div>
  );
  const Metric = ({ value, label, className }) => (
      <div className="flex items-baseline gap-1.5">
        <span className={"text-3xl font-bold tabular-nums " + className}>{value}</span>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
  );

  const next = summary?.nextReserved;
  return (
      <div className="flex flex-wrap gap-4 mb-6">
        <Card title="발송 예약">
          <div className="flex gap-6">
            <Metric value={n(summary?.reservedCount)} label="건 예약됨" className="text-emerald-600" />
            {/* 주의 = 예약 건 중 쿠폰 코드 미적재. 예약의 부분집합이라 이 박스에 함께 둔다(임시저장은 예약 시점에 따로 물어본다). */}
            <Metric value={n(summary?.warningCount)} label="건 주의" className={(summary?.warningCount ?? 0) > 0 ? "text-orange-500" : "text-gray-300"} />
          </div>
          <div className="mt-2 text-sm text-gray-600 truncate">
            {next
                ? <>다음 발송 <span className="font-medium text-gray-900">{formatSendTimeWithDay(next.issuedAt)}</span> · <span className="truncate">{next.campaignName}</span></>
                : <span className="text-gray-400">예정된 발송이 없습니다</span>}
          </div>
        </Card>
        <Card title="발송 결과">
          <div className="flex gap-6">
            <Metric value={n(summary?.succeedCount)} label="건 완료" className="text-blue-600" />
            <Metric value={n(summary?.failedCount)} label="건 실패" className="text-red-500" />
          </div>
          <div className="mt-2 text-sm text-gray-400">발송이 끝난 메시지 건수</div>
        </Card>
        <Card title="발송 관리">
          <Metric value={n(summary?.temporaryCount)} label="건 임시 저장" className="text-amber-500" />
          <div className="mt-2 text-sm text-gray-400">예약 전까지 발송되지 않는 작성 중 메시지</div>
        </Card>
      </div>
  );
};

/* ───────────────────────── 행 부속 ───────────────────────── */

/**
 * (i) 안내 버튼. **클릭**하면 안내 + 미리보기 팝업(`MessageInfoContent`)이 열린다.
 *
 * 처음엔 마우스 오버 팝오버였는데, 미리보기까지 담으면 아래로 길게 열려 화면이 떨리고
 * 스크롤로도 닿지 않았다(2026-10-15 로컬 테스트). 클릭 방식으로 바꾸면서 `...` 메뉴의
 * '미리보기' 와 같은 팝업을 쓴다. 눌러 볼 수 있어 보이도록 테두리가 있는 작은 버튼으로 그린다.
 * 경고(코드 부족)·실패가 있으면 색으로 먼저 알린다.
 */
const InfoButton = ({ onClick }) => {
  // 색은 넣지 않는다 — 상태 색은 상태 태그가, 코드 미적재는 주황 배지가 맡는다. 같은 뜻의 색이 두 곳에 있으면 체계가 흐려진다.
  return (
      <button
          type="button"
          aria-label="이 메시지 정보와 미리보기 열기"
          title="정보 미리보기"
          className="shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
      >
        <Info className="w-3.5 h-3.5" />
        정보
      </button>
  );
};

/** 안내 팝업 본문 — 안내 항목(코드 부족·실패 사유·비회원·수량) + 메시지 미리보기. (i) 버튼과 `...` 메뉴가 같은 내용을 쓴다. */
const MessageInfoContent = ({ message }) => {
  if (!message) return null;
  const status = STATUS[message.status] ?? { label: message.status, color: "default", showTime: false };
  const items = infoItemsOf(message);
  return (
      <div className="flex flex-col gap-4 text-sm min-w-[360px]">
        <div className="flex items-center gap-2 flex-wrap">
          <Tag color={status.color}>{status.label}</Tag>
          {status.showTime && <span className="text-gray-600">{formatSendTimeWithDay(message.issuedAt)}</span>}
          <span className="text-gray-400 text-xs ml-auto">생성 {formatDateTime(message.createdAt)}</span>
        </div>
        <div className="font-bold text-base break-words">{message.campaignName}</div>
        {items.length > 0 && (
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                  <div key={item.key}
                       className={"rounded-lg border p-3 " +
                           (item.tone === "error" ? "border-red-200 bg-red-50 text-red-700" :
                               item.tone === "warn" ? "border-orange-200 bg-orange-50 text-orange-700" :
                                   "border-gray-200 bg-gray-50 text-gray-700")}>
                    <div className="font-semibold flex items-center gap-1">
                      {item.tone === "warn" && <AlertTriangle className="w-4 h-4" />}
                      {item.title}
                    </div>
                    <div className="mt-1 whitespace-pre-wrap break-words">{item.body}</div>
                  </div>
              ))}
            </div>
        )}
        <div className="pt-3 border-t border-gray-100">
          <div className="text-xs font-semibold text-gray-500 mb-2">미리보기</div>
          <div className="flex justify-center">
            <MessageWrapPreview messageData={message.messageData} />
          </div>
        </div>
      </div>
  );
};

/**
 * 행의 `...` 메뉴. 실제 조작(수정·상세·예약 취소·삭제)을 한곳에 모았다.
 * 종전에는 '예약 취소'·'삭제' 버튼이 제목 옆에 늘어서 폭이 좁아지면 발송수 열과 겹쳤다.
 */
const RowMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (items.length === 0) return <div className="w-8" />;

  return (
      /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
      <div ref={wrapRef} className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
        <button
            type="button"
            aria-label="더 보기"
            aria-haspopup="menu"
            aria-expanded={open}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            onClick={() => setOpen((v) => !v)}
        >
          <EllipsisVertical className="w-5 h-5" />
        </button>
        {open && (
            <div role="menu" className="absolute right-0 top-full z-50 mt-1 min-w-[140px] rounded-xl border border-gray-200 bg-white shadow-xl py-1 text-sm">
              {items.map((item) => (
                  <button
                      key={item.key}
                      type="button"
                      role="menuitem"
                      className={"w-full text-left px-4 py-2 hover:bg-gray-50 " + (item.danger ? "text-red-600" : "text-gray-800")}
                      onClick={() => { setOpen(false); item.onClick(); }}
                  >
                    {item.label}
                  </button>
              ))}
            </div>
        )}
      </div>
  );
};

/**
 * 목록 한 행.
 *
 * 레이아웃: [타입] [제목 ─ 발송수 (i) ⚠] ……… [상태 + 발송시각] [생성일] [...]
 * 제목이 가장 큰 영역(flex-1)이고 나머지는 고정 폭이다. 폭이 좁아지면 오른쪽 묶음이 다음 줄로 내려가되
 * 5줄로 쪼개지지는 않는다(공용 GenericBoard 는 lg 아래에서 열마다 한 줄씩 세워 11개 다른 화면이 함께 쓰므로 건드리지 않았다).
 */
const MessageRow = ({ message, onRowClick, onOpenInfo, menuItems }) => {
  const status = STATUS[message.status] ?? { label: message.status, color: "default", showTime: false };
  const { success, total } = sendSummaryOf(message);
  const clickable = typeof onRowClick === "function";

  return (
      <div
          onClick={clickable ? onRowClick : undefined}
          role={clickable ? "button" : undefined}
          tabIndex={clickable ? 0 : undefined}
          onKeyDown={clickable ? (e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(); }
          } : undefined}
          className={"relative flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-blue-300 transition-all duration-200"
              + (clickable ? " cursor-pointer" : "")}
      >
        {/* 타입 */}
        <div className="shrink-0 w-28">
          <Tag>{messageTypeLabel(message.messageType)}</Tag>
        </div>

        {/* 제목 + 발송수 + (i) + 경고 — 가장 큰 영역 */}
        <div className="flex-1 min-w-[240px] flex items-center gap-2">
          <div className={"min-w-0 text-base font-semibold truncate " +
              (message.status === "FAILED" ? "text-red-600" :
                  message.status === "TEMPORARY" ? "text-gray-500" : "text-gray-900")}>
            {message.campaignName}
          </div>
          {total > 0 && (
              <span className="shrink-0 text-xs text-gray-400 tabular-nums">
                {success.toLocaleString()} / {total.toLocaleString()}
                {!isBeforeSend(message.status) && ` (${Math.round(success / total * 100)}%)`}
              </span>
          )}
          <InfoButton onClick={onOpenInfo} />
          {message.codeShortText && (
              <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5"
                    title={message.codeShortText}>
                <AlertTriangle className="w-3.5 h-3.5" />
                코드 미적재
              </span>
          )}
        </div>

        {/* 오른쪽 묶음: 상태+발송시각 / 생성일 / 메뉴 */}
        <div className="flex items-center gap-5 ml-auto shrink-0">
          <div className="flex items-center gap-2 w-44">
            <Tag color={status.color}>{status.label}</Tag>
            {status.showTime && (
                <span className="text-sm text-gray-600 tabular-nums whitespace-nowrap">{formatSendTime(message.issuedAt)}</span>
            )}
          </div>
          <div className="w-36 text-right text-sm text-gray-500 tabular-nums whitespace-nowrap">
            <span className="text-xs text-gray-400 mr-1">생성</span>{formatDateTime(message.createdAt)}
          </div>
          <RowMenu items={menuItems} />
        </div>
      </div>
  );
};

/* ───────────────────────── 화면 ───────────────────────── */

const VolatilityMessageComponent = ({type}) => {
  // null = "전체"(기간 조건 없음). 초기 탭이 "전체" 이므로 null 로 시작한다. 기간은 항상 **생성일** 기준이다.
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  // "ISSUED_AT"(발송일순, 기본) | "CREATED_AT"(생성일순). 2026-10-23 사용자 지시로 기본을 발송일순으로 되돌렸다 —
  // 예약 건이 위로 오고 발송이 끝난 것은 발송일 순(서버 ListSort.ISSUED_AT). 복제본이 뒤로 밀리는 것은 생성일순 탭으로 본다.
  const [sort, setSort] = useState("ISSUED_AT");
  const [totalCount, setTotalCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [previewMessage, setPreviewMessage] = useState(null);
  const navigate = useNavigate();

  const dateItems = [
    { value: "all", label: "전체", selected: true },
    { value: "month", label: "최근 한 달" },
    { value: "week", label: "최근 일주일" },
  ];

  const sortItems = [
    { value: "ISSUED_AT", label: "발송일순", selected: true },
    { value: "CREATED_AT", label: "생성일순" },
  ];

  useEffect(() => {
    getVolatilityMessages(dateRange?.startDate, dateRange?.endDate, type, page - 1, PAGE_SIZE, sort)
        .then(({data}) => {
          setMessages(data.messages);
          setTotalCount(data.count);
        });
  }, [dateRange, page, sort]);

  useEffect(() => {
    getVolatilityMessageSummary(dateRange?.startDate, dateRange?.endDate, type).then(setSummary);
  }, [dateRange]);

  const handleQuickChange = (quick) => {
    const format = "YYYY-MM-DD";
    const today = moment().format(format);
    setPage(1);
    if (quick === "all") {
      setDateRange(null);
    } else if (quick === "month") {
      setDateRange({ startDate: moment().subtract(1, "months").format(format), endDate: today });
    } else if (quick === "week") {
      setDateRange({ startDate: moment().subtract(6, "days").format(format), endDate: today });
    }
  };

  const onDateInput = (range) => {
    setPage(1);
    setDateRange({
      startDate: moment(range.startDate).format("yyyy-MM-DD"),
      endDate: moment(range.endDate).format("yyyy-MM-DD")
    });
  };

  const handleCreateClick = () => {
    window.location.href = "/message/onetime/edit";
  };

  const openDetail = (message) => navigate("/volatility-message/detail?id=" + message.id);
  const openEdit = (message) => navigate("/message/onetime/edit?id=" + message.id);

  /**
   * 행 전체 클릭 시 이동 지점: **발송 전(예약·임시저장)은 수정 화면, 발송 후는 상세 화면.**
   *
   * 예약 건을 미리보기 팝업으로 보내던 종전 동작(커밋 9803d499 이래)은 '정보' 버튼과 겹치고 수정 경로가 없어
   * 2026-10-15 에 바꿨다. 수정 화면은 예약 상태를 상단 배너로 알리고, 저장하면 예약이 임시 저장으로 바뀐다.
   * 집계/분석 중인 건은 아직 열어볼 화면이 없어 아무 동작도 하지 않는다.
   */
  const handleRowClick = (message) => {
    if (message.status === "RESERVED" || message.status === "TEMPORARY") {
      openEdit(message);
    } else if (["FAILED", "SUCCEED", "SENDING"].includes(message.status)) {
      openDetail(message);
    }
  };

  const cancelReservedMessage = (id) => {
    if (window.confirm("예약을 취소하시겠습니까?\n임시 저장 상태로 변경됩니다.")) {
      cancelReserved(id).then(() => {
        window.location.reload();
      });
    }
  };

  /**
   * 미발송(임시저장·예약) 메시지 삭제.
   * 발송 이력이 있는 상태는 서버가 거부하므로 메뉴 항목 자체를 노출하지 않는다(이중 방어).
   */
  const deleteMessage = (id, status, isSendMoment) => {
    const lines = ["이 메시지를 완전히 삭제하시겠습니까?"];
    if (status === "RESERVED") lines.push("", "예약이 취소되고 발송되지 않습니다.");
    // 비회원 포함 예약은 저장 시점에 카카오로 친구 그룹 파일이 이미 올라가 있고,
    // 그 파일은 이 삭제로 정리되지 않는다(카카오 삭제 API 미연동).
    if (isSendMoment) lines.push("", "비회원 발송이 포함된 예약입니다. 카카오에 올라간 발송 제외 목록 파일은 함께 정리되지 않으니 담당자에게 알려주세요.");
    lines.push("", "발송 대상·조건 정보도 함께 삭제되며 되돌릴 수 없습니다.");
    if (window.confirm(lines.join("\n"))) {
      deleteVolatilityMessage(id).then((ok) => {
        if (!ok) {
          alert("삭제할 수 없는 상태입니다. 발송이 진행된 메시지는 이력 보존을 위해 삭제되지 않습니다.");
          return;
        }
        window.location.reload();
      });
    }
  };

  /** `...` 메뉴 항목 — 상태별로 가능한 조작만 넣는다. */
  const menuItemsOf = (message) => {
    const { id, status, isSendMoment } = message;
    const items = [{ key: "info", label: "정보 미리보기", onClick: () => setPreviewMessage(message) }];
    if (["FAILED", "SUCCEED", "SENDING"].includes(status)) {
      items.push({ key: "detail", label: "상세 보기", onClick: () => openDetail(message) });
    }
    if (status === "TEMPORARY" || status === "RESERVED") {
      items.push({ key: "edit", label: "수정", onClick: () => openEdit(message) });
    }
    if (status === "RESERVED") {
      items.push({ key: "cancel", label: "예약 취소", onClick: () => cancelReservedMessage(id) });
    }
    if (status === "RESERVED" || status === "TEMPORARY") {
      items.push({ key: "delete", label: "삭제", danger: true, onClick: () => deleteMessage(id, status, isSendMoment) });
    }
    return items;
  };

  return (
    <>
      <div className="bg-white relative border border-gray-300 rounded-lg flex flex-wrap gap-4 mb-6 p-4 items-center" style={{marginTop: "35px"}}>
        <div className="flex flex-wrap gap-4 items-center">
          <Tab
              variants="input"
              items={dateItems}
              onChanged={(data) => handleQuickChange(data.value)}
          />
          {/* dateRange 가 null("전체") 이면 initDate 를 주지 않는다 → CalendarBar 가 '전체 ~ 전체' 로 표시한다.
              CalendarBar 는 initDate 가 truthy 일 때만 내부 상태를 갱신하므로, 날짜→전체 전환 때 이전 날짜가
              남지 않도록 key 로 다시 마운트한다. */}
          <CalendarBar key={dateRange ? "ranged" : "all"}
                       initDate={dateRange ? {startDate: moment(dateRange.startDate).toDate(), endDate: moment(dateRange.endDate).toDate()} : undefined}
                       onConfirm={(props) => onDateInput(props)} />
          <span className="text-xs text-gray-400">기간은 생성일 기준</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-500">정렬</span>
          <Tab
              variants="input"
              items={sortItems}
              onChanged={(data) => { setSort(data.value); setPage(1); }}
          />
        </div>
      </div>

      <SummaryCards summary={summary} />

      <div>
        <div className="flex-align-center justify-end">
          {
            type === "ONETIME" && (
                <div className="flex-align-center gap-4">
                  {/*
                    'AI 메시지 자동 생성' 버튼을 지웠다(2026-09-10, 사용자 지시).

                    이유 = 제대로 동작하지 않고 실사용도 없었다. 눌렀는데 아무 일도 안 일어나는 버튼을
                    화면 상단에 남겨 두면 담당자가 매번 "이건 되는 기능인가" 를 다시 확인해야 한다.

                    🔑 **버튼만 뺀다.** 목적지인 `/ai/message/generator` 라우트와
                       `AiMessageGenerator.js` 화면은 그대로 둔다 — 주소를 직접 아는 사람이 있을 수 있고,
                       화면 자체의 제거는 이 지시의 범위가 아니다. 되살리려면 이 자리에 `Sparkles`
                       아이콘 버튼을 다시 넣으면 된다.
                  */}
                  <Button
                      variants="primary"
                      onClick={() => handleCreateClick(true)}
                      size="large"
                      value="메시지 보내기"
                  />
                </div>
              )
          }
        </div>
        <div className="flex-col py-4">
          <div className="w-full bg-gray-50/50 p-4 font-sans">
            <div className="mx-auto flex flex-col gap-3">
              {messages.map((message) => (
                  <MessageRow
                      key={message.id}
                      message={message}
                      onRowClick={() => handleRowClick(message)}
                      onOpenInfo={() => setPreviewMessage(message)}
                      menuItems={menuItemsOf(message)}
                  />
              ))}
              {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100 border-dashed">
                    <p className="text-gray-400 font-medium">표시할 데이터가 없습니다.</p>
                  </div>
              )}
            </div>
          </div>
          <div className="flex justify-center">
            <Pagination pageSize={PAGE_SIZE} totalCount={totalCount} currentPage={page} onPaging={(p) => setPage(p)} />
          </div>
        </div>
      </div>

      {/* '정보' 버튼과 `...` 메뉴 '정보 미리보기' 가 이 팝업을 연다. 행 클릭은 수정/상세 화면으로 간다. */}
      <SuspensePopup className="modal-fit" visible={previewMessage !== null} title="메시지 정보" onClose={() => setPreviewMessage(null)} >
        <MessageInfoContent message={previewMessage} />
      </SuspensePopup>
    </>
  );
};
export default VolatilityMessageComponent;
